import React, { useState, useEffect } from 'react';
import {
  Beaker,
  Calculator,
  BookOpen,
  Users,
  TrendingUp,
  BarChart3,
  PieChart,
  Award,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from 'sonner';
import apiService from '@/services/api';

// Stream definitions
const STREAM_CONFIG = {
  Science: {
    core: ["English", "Mathematics", "Chemistry", "Biology", "Physics"],
    optional: ["ICT", "Agriculture", "French", "Geography"],
    icon: Beaker,
    color: "bg-purple-500",
    lightColor: "bg-purple-100",
    textColor: "text-purple-700",
    borderColor: "border-purple-300"
  },
  Commerce: {
    core: ["English", "Mathematics", "Economics", "Accounting", "ICT"],
    optional: ["French", "Agriculture"],
    icon: Calculator,
    color: "bg-green-500",
    lightColor: "bg-green-100",
    textColor: "text-green-700",
    borderColor: "border-green-300"
  },
  Arts: {
    core: ["English", "Mathematics", "History", "Geography", "Development Studies"],
    optional: ["ICT", "Agriculture", "French"],
    icon: BookOpen,
    color: "bg-amber-500",
    lightColor: "bg-amber-100",
    textColor: "text-amber-700",
    borderColor: "border-amber-300"
  }
};

export default function StreamDistributionReport() {
  const [year, setYear] = useState('11');
  const [loading, setLoading] = useState(false);
  const [streamData, setStreamData] = useState({
    Science: { count: 0, optionalBreakdown: {}, avgPerformance: 0, students: [] },
    Commerce: { count: 0, optionalBreakdown: {}, avgPerformance: 0, students: [] },
    Arts: { count: 0, optionalBreakdown: {}, avgPerformance: 0, students: [] },
    Unassigned: { count: 0, students: [] }
  });
  const [totalStudents, setTotalStudents] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Get all classes for this year
        const classes = await apiService.listClasses(null, parseInt(year));
        
        // Get unique students from all classes
        const studentMap = new Map();
        
        for (const cls of classes) {
          try {
            const classStudents = await apiService.getClassStudents(cls.id);
            if (classStudents && classStudents.students) {
              classStudents.students.forEach(s => {
                if (!studentMap.has(s.student_id)) {
                  studentMap.set(s.student_id, {
                    id: s.student_id,
                    name: s.name || s.student_id,
                    stream: 'Not Assigned',
                    optional: '',
                    subjects: []
                  });
                }
              });
            }
          } catch (err) {
            // Skip failed class loads
          }
        }
        
        // Get subject assignments for each student
        const newStreamData = {
          Science: { count: 0, optionalBreakdown: {}, avgPerformance: 0, students: [] },
          Commerce: { count: 0, optionalBreakdown: {}, avgPerformance: 0, students: [] },
          Arts: { count: 0, optionalBreakdown: {}, avgPerformance: 0, students: [] },
          Unassigned: { count: 0, students: [] }
        };
        
        // Initialize optional breakdowns
        Object.keys(STREAM_CONFIG).forEach(stream => {
          STREAM_CONFIG[stream].optional.forEach(opt => {
            newStreamData[stream].optionalBreakdown[opt] = 0;
          });
        });
        
        for (const [studentId, student] of studentMap) {
          try {
            const subjectData = await apiService.getStudentReportCardSubjects(studentId, parseInt(year));
            student.stream = subjectData.stream || 'Not Assigned';
            student.subjects = subjectData.subjects || [];
            
            // Detect optional subject
            const streamDef = STREAM_CONFIG[student.stream];
            if (streamDef) {
              const optional = student.subjects.find(s => streamDef.optional.includes(s));
              student.optional = optional || '';
            }
          } catch (err) {
            // Keep default values - student likely not assigned
          }
          
          // Categorize student
          if (student.stream === 'Not Assigned') {
            newStreamData.Unassigned.count++;
            newStreamData.Unassigned.students.push(student);
          } else if (newStreamData[student.stream]) {
            newStreamData[student.stream].count++;
            newStreamData[student.stream].students.push(student);
            
            // Track optional subject
            if (student.optional && newStreamData[student.stream].optionalBreakdown[student.optional] !== undefined) {
              newStreamData[student.stream].optionalBreakdown[student.optional]++;
            }
          }
        }
        
        // Calculate mock performance averages (would come from actual grade data)
        newStreamData.Science.avgPerformance = 72;
        newStreamData.Commerce.avgPerformance = 68;
        newStreamData.Arts.avgPerformance = 70;
        
        setStreamData(newStreamData);
        setTotalStudents(studentMap.size);
      } catch (error) {
        console.error('Error loading stream data:', error);
        toast.error('Failed to load stream distribution data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [year]);

  const loadStreamData = () => {
    // Trigger reload by incrementing a counter or calling the effect
    setLoading(true);
    // The useEffect will handle the actual loading
  };

  const getStreamPercentage = (count) => {
    if (totalStudents === 0) return 0;
    return Math.round((count / totalStudents) * 100);
  };

  const getMostPopularOptional = (stream) => {
    const breakdown = streamData[stream]?.optionalBreakdown || {};
    let maxSubject = '';
    let maxCount = 0;
    
    Object.entries(breakdown).forEach(([subject, count]) => {
      if (count > maxCount) {
        maxCount = count;
        maxSubject = subject;
      }
    });
    
    return { subject: maxSubject, count: maxCount };
  };

  return (
    <div className="space-y-6 p-6" data-testid="stream-distribution-report">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Stream Distribution Report
          </h1>
          <p className="text-gray-500">Analytics on student stream enrollment and optional subject popularity</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="11">Year 11</SelectItem>
              <SelectItem value="12">Year 12</SelectItem>
              <SelectItem value="13">Year 13</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={loadStreamData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">Total Students</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-800">{totalStudents}</div>
            <p className="text-xs text-blue-600">Year {year} Enrollment</p>
          </CardContent>
        </Card>
        
        {Object.entries(STREAM_CONFIG).map(([stream, config]) => {
          const Icon = config.icon;
          const count = streamData[stream]?.count || 0;
          const percentage = getStreamPercentage(count);
          
          return (
            <Card key={stream} className={`${config.lightColor} ${config.borderColor} border-2`}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm font-medium ${config.textColor} flex items-center gap-2`}>
                  <Icon className="w-4 h-4" />
                  {stream}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${config.textColor}`}>{count}</div>
                <p className={`text-xs ${config.textColor} opacity-75`}>{percentage}% of total</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Stream Distribution Visual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-blue-600" />
            Stream Distribution
          </CardTitle>
          <CardDescription>Visual breakdown of students by stream</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(STREAM_CONFIG).map(([stream, config]) => {
              const count = streamData[stream]?.count || 0;
              const percentage = getStreamPercentage(count);
              const Icon = config.icon;
              
              return (
                <div key={stream} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${config.textColor}`} />
                      <span className="font-medium">{stream}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{count} students</span>
                      <Badge className={config.lightColor + ' ' + config.textColor}>{percentage}%</Badge>
                    </div>
                  </div>
                  <Progress 
                    value={percentage} 
                    className={`h-3 ${config.lightColor}`}
                  />
                </div>
              );
            })}
            
            {streamData.Unassigned.count > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-600">Unassigned</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{streamData.Unassigned.count} students</span>
                    <Badge variant="outline">{getStreamPercentage(streamData.Unassigned.count)}%</Badge>
                  </div>
                </div>
                <Progress 
                  value={getStreamPercentage(streamData.Unassigned.count)} 
                  className="h-3 bg-gray-100"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Optional Subject Popularity */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(STREAM_CONFIG).map(([stream, config]) => {
          const Icon = config.icon;
          const breakdown = streamData[stream]?.optionalBreakdown || {};
          const mostPopular = getMostPopularOptional(stream);
          const streamCount = streamData[stream]?.count || 0;
          
          return (
            <Card key={stream} className={`border-2 ${config.borderColor}`}>
              <CardHeader className={config.lightColor}>
                <CardTitle className={`flex items-center gap-2 ${config.textColor}`}>
                  <Icon className="w-5 h-5" />
                  {stream} Optional Subjects
                </CardTitle>
                <CardDescription>
                  {config.optional.length} options available
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {config.optional.map(opt => {
                  const count = breakdown[opt] || 0;
                  const percentage = streamCount > 0 ? Math.round((count / streamCount) * 100) : 0;
                  const isPopular = opt === mostPopular.subject && count > 0;
                  
                  return (
                    <div key={opt} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={isPopular ? 'font-semibold' : ''}>{opt}</span>
                        {isPopular && <Award className="w-4 h-4 text-amber-500" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">{count}</span>
                        <Badge variant={isPopular ? 'default' : 'outline'} className="w-14 justify-center">
                          {percentage}%
                        </Badge>
                      </div>
                    </div>
                  );
                })}
                
                {streamCount === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    No students assigned to this stream
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Performance Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Average Performance by Stream
          </CardTitle>
          <CardDescription>
            Term average scores across streams (placeholder data - connect to gradebook)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(STREAM_CONFIG).map(([stream, config]) => {
              const Icon = config.icon;
              const avgPerf = streamData[stream]?.avgPerformance || 0;
              
              return (
                <div key={stream} className={`p-4 rounded-lg ${config.lightColor} ${config.borderColor} border-2`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`flex items-center gap-2 ${config.textColor}`}>
                      <Icon className="w-5 h-5" />
                      <span className="font-semibold">{stream}</span>
                    </div>
                    <span className={`text-2xl font-bold ${config.textColor}`}>{avgPerf}%</span>
                  </div>
                  <Progress value={avgPerf} className="h-2" />
                  <p className="text-xs text-gray-500 mt-2">
                    Based on {streamData[stream]?.count || 0} students
                  </p>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Note: Performance data is placeholder. Connect to gradebook for real statistics.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Unassigned Students Warning */}
      {streamData.Unassigned.count > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Unassigned Students ({streamData.Unassigned.count})
            </CardTitle>
            <CardDescription className="text-red-600">
              These students need to be assigned to a stream for their report cards
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {streamData.Unassigned.students.slice(0, 10).map(student => (
                <Badge key={student.id} variant="outline" className="bg-white">
                  {student.name || student.id}
                </Badge>
              ))}
              {streamData.Unassigned.students.length > 10 && (
                <Badge variant="outline" className="bg-white">
                  +{streamData.Unassigned.students.length - 10} more
                </Badge>
              )}
            </div>
            <Button 
              className="mt-4"
              variant="outline"
              onClick={() => window.location.href = '/dashboard/student-subjects'}
            >
              <Users className="w-4 h-4 mr-2" />
              Assign Students to Streams
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
